import { List as ImList, Map as ImMap, Set as ImSet, Record, type RecordOf } from 'immutable';
import mapValues from 'lodash/mapValues';

import type { Account } from 'app-components/flux/accounts/accountsTypes';
import type { QcsSyncResourceStoreProps } from 'app-components/flux/core/resourceStoreTypes';
import type { institutionsTypes } from 'app-components/flux/institutions';

export type InstitutionLoginQcsAction = 'close' | 'disconnect' | 'make-manual';
export type InstitutionLoginStatus =
  | 'OK'
  | 'ACTION_REQUIRED'
  | 'AGGREGATOR_IN_ERROR'
  | 'FAILED_WHEN_POLLING'
  | 'DONE_PROCESSING_WITH_UPDATE'
  | 'DONE_PROCESSING_WITHOUT_UPDATE'
  | 'TIMEOUT_WHILE_PROCESSING'
  | 'NO_ACCOUNTS_TO_REFRESH';
export type AggStatus =
  | 'OK'
  | 'NEW'
  | 'MFA_CHALLENGE_READY_FOR_USER'
  | 'DISCOVERED_ACCOUNTS_READY_FOR_USER'
  | 'NO_ACCOUNTS_REFRESHED'
  | 'GENERIC'
  | 'CP_SERVER_ERROR'
  | 'REAUTHENTICATION_REQUIRED'
  | 'CHANNEL_RESET_REQUIRED'
  | 'INVALID_CREDENTIALS'
  | 'QUEUED_MFA_CHALLENGE'
  | 'ACCOUNT_MISMATCH'
  | 'ACCOUNT_NOT_FOUND'
  | 'CP_ACCOUNT_NOT_FOUND';

export type AggErrorDismissalKey = AggStatus | 'ACCOUNT_AGG_ERROR';

export type RefreshFrequency =
  | 'NONE'
  | 'DAILY'
  | 'WEEKLY'
  | 'BIWEEKLY'
  | 'MONTHLY'
  | 'BIMONTHLY'
  | 'QUARTERLY'
  | 'HALF_YEARLY'
  | 'YEARLY'
  | 'NEVER';

export type InstitutionLoginAggregator = {
  channel?: string | null;
  cpId?: string | null;

  aggStatus?: AggStatus | null;
  aggStatusCode?: string | null;
  aggStatusDetail?: string | null;

  lastStatusUpdatedAt?: string | null;

  lastRefreshSuccessfulAt?: string | null;
  lastRefreshAttemptedAt?: string | null;
  nextManualRefreshEligibleAt?: string | null;
};

export const mkInstitutionLoginAggregator = Record<InstitutionLoginAggregator>({
  channel: null,
  cpId: null,

  aggStatus: null,
  aggStatusCode: null,
  aggStatusDetail: null,

  lastStatusUpdatedAt: null,

  lastRefreshSuccessfulAt: null,
  lastRefreshAttemptedAt: null,
  nextManualRefreshEligibleAt: null,
});

export type Credential = {
  key: string;
  // No value means the user did not supply one; it must stay absent from QCS
  // request payloads rather than be filled with a placeholder.
  value?: string;
};

export const mkCredential = Record<Credential>({
  key: 'unknown',
  value: undefined,
});

export type FiMigrationType = 'NONE' | 'JOB_MIGRATION' | 'OPTIONAL_MIGRATION' | 'FORCED_MIGRATION';

export type FiMigrationInfo = {
  toInstitutionId: string;
  toChannel: string;
  fiMigrationType?: FiMigrationType | null;
};

export const mkFiMigrationInfo = Record<FiMigrationInfo>({
  toInstitutionId: 'unknown',
  toChannel: 'unknown',
  fiMigrationType: null,
});

export type InstitutionLoginProps = {
  clientId?: string;
  id: string;
  name: string;
  modifiedAt?: string;

  institutionId?: string | null;
  brandingId?: string | null;
  cpInstitutionId?: string | null;

  ewcLive?: boolean | null;

  cpSetupMode?: string | null;
  channel?: string | null;
  credentials?: ImList<RecordOf<Credential>> | null;

  isConnected?: boolean | null;
  aggregators?: ImList<InstitutionLoginAggregator> | null;

  optionalAction?: string | null;
  fiMigrationInfo?: RecordOf<FiMigrationInfo> | null;
  isDeleted?: boolean;
  fiIssue?: RecordOf<institutionsTypes.FiIssue> | null;
  refreshFrequency?: RefreshFrequency;
};

export type InstitutionLogin = RecordOf<InstitutionLoginProps>;

const institutionLoginDefaults: InstitutionLoginProps = {
  clientId: undefined,
  id: '0', // TODO: set valid default
  name: 'unknown', // TODO: set valid default
  modifiedAt: undefined,

  institutionId: null, // TODO: set valid default
  brandingId: null, // TODO: set valid default
  cpInstitutionId: null, // TODO: set valid default

  ewcLive: null,

  cpSetupMode: 'SKIP_ACCOUNT_DISCOVERY',
  channel: null,
  credentials: null,

  isConnected: null, // TODO: set valid default
  aggregators: null, // TODO: set valid default

  optionalAction: null,
  fiMigrationInfo: null,

  isDeleted: undefined,
  fiIssue: undefined,
  refreshFrequency: 'NONE',
};

export const mkInstitutionLogin = Record<InstitutionLoginProps>(institutionLoginDefaults);
// safest way to update just selected properties and ignore others
export const mkInstitutionLoginPatch = Record<Partial<InstitutionLoginProps>>(
  mapValues(institutionLoginDefaults, () => undefined),
);

// Created on the client and not assigned an id by QCS yet
export type InstitutionLoginClient = Omit<InstitutionLoginProps, 'id'> & {
  id?: string;
};
export const mkInstitutionLoginClient = Record<InstitutionLoginClient>({
  ...institutionLoginDefaults,
  id: undefined,
});

// ================================================================================================
// MfaChallenge & MfaResponse
// ================================================================================================

export type MfaAnswerChoice = {
  displayText: string;
};

export const mkMfaAnswerChoice = Record<MfaAnswerChoice>({
  displayText: 'unknown',
});

export type MfaChallengeItem = {
  type: string;

  key: string;
  sequence: 0;

  question: string;
  base64Image?: string | null;
  answerChoices?: ImList<RecordOf<MfaAnswerChoice>> | null;
};

export const mkMfaChallengeItem = Record<MfaChallengeItem>({
  type: 'QUESTION',

  key: '0',
  sequence: 0,

  base64Image: null,
  question: 'unknown',
  answerChoices: null,
});

export type MfaChallenge = {
  institutionLoginId: string;
  challenges: ImList<MfaChallengeItem>;

  submitUrl: string;
};

export const mkMfaChallenge = Record<MfaChallenge>({
  institutionLoginId: '0',
  challenges: ImList(),

  submitUrl: '',
});

export type MfaAnswer = {
  id: string;
  value: string;
};

export const mkMfaAnswer = Record<MfaAnswer>({
  id: '0',
  value: 'unknown',
});

export type MfaResponse = {
  institutionLoginId: string;
  answers: ImList<MfaAnswer>;
  submitUrl: string;
};

export const mkMfaResponse = Record<MfaResponse>({
  institutionLoginId: '0',
  answers: ImList(),
  submitUrl: 'need_to_set_submit_url',
});

export class RefreshAccountsMfaFormSubmitError extends Error {
  institutionLoginId: string;
  constructor(message: string, institutionLoginId: string) {
    super(message);
    this.institutionLoginId = institutionLoginId;
  }
}

// ================================================================================================
// RefreshResponse -e
// ================================================================================================

export type RefreshAggregator = {
  channel?: string;

  isProcessing?: boolean;
  aggStatus?: AggStatus;
  cpAggStatusCode?: string | null;
  cpAggStatusDetail?: string;

  mfaChallenges?: MfaChallengeItem[];
  accounts?: ImList<Account>;
};

export const makeRefreshAggregator = Record<RefreshAggregator>({
  channel: undefined,

  isProcessing: false,
  aggStatus: undefined,
  cpAggStatusCode: undefined,
  cpAggStatusDetail: undefined,

  mfaChallenges: undefined,
  accounts: undefined,
});

export type RefreshInstitutionLogin = {
  id?: string;

  isProcessing?: boolean;
  status: InstitutionLoginStatus;
  credentialsBlob?: string;
  submitUrl?: string;

  aggregators?: ImList<RefreshAggregator> | null;
};

export const makeRefreshInstitutionLogin = Record<RefreshInstitutionLogin>({
  id: '0',

  isProcessing: false,
  status: 'OK',

  credentialsBlob: undefined,
  submitUrl: undefined,

  aggregators: undefined,
});

export type RefreshResponse = {
  jobId: string;

  isProcessing?: boolean;
  status: InstitutionLoginStatus;

  institutionLogins: ImList<RefreshInstitutionLogin>;
};

export const makeRefreshResponse = Record<RefreshResponse>({
  jobId: '0',

  isProcessing: false,
  status: 'OK',

  institutionLogins: ImList(),
});

export type InstitutionLoginStore = QcsSyncResourceStoreProps<InstitutionLoginProps> & {
  isRefreshingAll: boolean;
  idsRefreshing: ImSet<string>;

  idsRefreshingWithUserActionsPending: ImSet<string>;
  idsRefreshingWithCancelledUserActions: ImSet<string>;

  credentialBlobsForRefreshingLogins: ImMap<string, string>;
  credentialsForRefreshingLogins: ImMap<string, ImList<Credential>>;
  /** Maps login ids to issue type in order to hide issues for a session. This key is not persisted  */
  dismissedFiIssuesByLoginId: ImMap<string, institutionsTypes.FiIssueType>;
  /** Maps login ids to agg error keys in order to hide issues for a session. This key is not persisted */
  dismissedAggStatusesByLoginId: ImMap<string, AggErrorDismissalKey>;
  /** Hides the optional migration summary for the current session. This key is not persisted. */
  fiMigrationBannerDismissed: boolean;
};

export const mkInstitutionLoginsStore = Record<InstitutionLoginStore>({
  resourcesById: ImMap(),
  lastSyncDate: null,

  loadPending: true,
  isLoading: false,

  isRefreshingAll: false,
  idsRefreshing: ImSet(),

  idsRefreshingWithUserActionsPending: ImSet(),
  idsRefreshingWithCancelledUserActions: ImSet(),

  credentialBlobsForRefreshingLogins: ImMap(),
  credentialsForRefreshingLogins: ImMap(),

  dismissedFiIssuesByLoginId: ImMap(),
  dismissedAggStatusesByLoginId: ImMap(),
  fiMigrationBannerDismissed: false,
});

export const INSTITUTION_LOGINS_REDUCER_KEY = 'institutionLoginsStore';

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [INSTITUTION_LOGINS_REDUCER_KEY]: RecordOf<InstitutionLoginStore>;
  }
}
